/* eslint-disable @next/next/no-img-element */
"use client";

import { useState, useEffect, useRef, Suspense } from 'react';
import { useRouter } from 'next/navigation';
import {
  MapPin, Building2, UserCircle2, ArrowLeft, Loader2, Sparkles,
  AlertCircle, Check, ChevronRight, Plus, X, Phone, Navigation,
  FileText, Upload, Hash, Mail, Lock
} from 'lucide-react';
import { signInWithPopup, GoogleAuthProvider, OAuthProvider, createUserWithEmailAndPassword } from 'firebase/auth';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { useTrialConfig } from '@/hooks/useTrialConfig';
import { auth, db } from '@/lib/firebase/client';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { CATEGORIES } from '@/lib/categories';

const TOTAL_STEPS = 5;

function BusinessRegisterContent() {
  const router = useRouter();
  const { t } = useTranslation('common');
  const { trialDays: trialDaysConfig } = useTrialConfig();
  const { user, userData, loading: authLoading } = useAuth();

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Auth step — if user is already logged in, jump to step 2
  useEffect(() => {
    if (!authLoading && user && step === 1) {
      setStep(2);
    }
  }, [authLoading, user, step]);

  // Auto-fill contact info from user profile
  useEffect(() => {
    if (!user) return;
    
    const nameToUse = user.displayName || userData?.name || '';
    if (nameToUse) {
      if (!businessName) setBusinessName(nameToUse);
    }
    
    const phoneToUse = user.phoneNumber || userData?.phone || '';
    if (phoneToUse && !phone) {
      setPhone(phoneToUse);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, userData]);

  // Form State
  const [roleType, setRoleType] = useState<'salon' | 'master'>('master');
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [subCategories, setSubCategories] = useState<string[]>([]);
  const [customSubCategory, setCustomSubCategory] = useState('');

  // Location — Nominatim-based autocomplete (same approach as Dashboard Settings)
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [lat, setLat] = useState<number | null>(null);
  const [lng, setLng] = useState<number | null>(null);
  const [addressSuggestions, setAddressSuggestions] = useState<{display_name: string; lat: string; lon: string}[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [geoLoading, setGeoLoading] = useState(false);
  const addressTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Profile data
  const [businessName, setBusinessName] = useState('');
  const [phone, setPhone] = useState('');
  const [businessNumber, setBusinessNumber] = useState('');
  const [about, setAbout] = useState('');
  const [instagramHandle, setInstagramHandle] = useState('');
  const [certificates, setCertificates] = useState<string[]>([]);
  const [certUploading, setCertUploading] = useState(false);
  const certInputRef = useRef<HTMLInputElement>(null);

  // Auth step: email/password fields
  const [authEmail, setAuthEmail] = useState('');
  const [authPassword, setAuthPassword] = useState('');

  // Nominatim address autocomplete with debounce
  const searchAddress = (query: string) => {
    setAddress(query);
    if (addressTimeoutRef.current) clearTimeout(addressTimeoutRef.current);
    if (query.length < 2) { setAddressSuggestions([]); setShowSuggestions(false); return; }
    addressTimeoutRef.current = setTimeout(async () => {
      try {
        const res = await fetch(`https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query)}&format=json&addressdetails=1&limit=5&countrycodes=il&accept-language=he`);
        const data = await res.json();
        setAddressSuggestions(data);
        setShowSuggestions(data.length > 0);
      } catch { setAddressSuggestions([]); }
    }, 400);
  };

  const selectAddress = (suggestion: {display_name: string; lat: string; lon: string}) => {
    setAddress(suggestion.display_name);
    setLat(parseFloat(suggestion.lat));
    setLng(parseFloat(suggestion.lon));
    setShowSuggestions(false);
    setAddressSuggestions([]);

    // Auto-detect city from display_name (Nominatim)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const raw = suggestion as any;
    if (raw.address) {
      const detectedCity = raw.address.city || raw.address.town || raw.address.village || '';
      if (detectedCity) setCity(detectedCity);
    }
  };

  // Geolocation detect
  const detectLocation = () => {
    setGeoLoading(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const latitude = pos.coords.latitude;
        const longitude = pos.coords.longitude;
        setLat(latitude);
        setLng(longitude);
        try {
          const res = await fetch(`https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json&accept-language=he`);
          const data = await res.json();
          if (data.display_name) {
            setAddress(data.display_name);
            const detectedCity = data.address?.city || data.address?.town || data.address?.village || '';
            if (detectedCity) setCity(detectedCity);
          }
        } catch { /* ignore */ }
        setGeoLoading(false);
      },
      () => {
        setError(t('dashboard_extra.geo_error'));
        setGeoLoading(false);
      }
    );
  };

  // Multi-category toggle
  const toggleCategory = (catKey: string) => {
    setSelectedCategories(prev =>
      prev.includes(catKey)
        ? prev.filter(k => k !== catKey)
        : [...prev, catKey]
    );
  };

  // Get all subcategories for selected categories
  const availableSubs = CATEGORIES
    .filter(c => selectedCategories.includes(c.key))
    .flatMap(c => c.subs);

  const handleNextStep = () => {
    if (step === 1 && !user) return setError(t('register_flow.auth_required'));
    if (step === 3 && selectedCategories.length === 0) return setError(t('auth.select_category'));
    if (step === 4 && (!address || address.trim().length < 3)) return setError(t('auth.address_error'));
    setError('');
    setStep(s => s + 1);
  };

  const finalizeRegistration = async () => {
    if (!user) return;
    setLoading(true);
    try {
      const uid = user.uid;
      const authEmailVal = user.email || '';
      const authName = user.displayName || businessName || '';

      const userDoc = await getDoc(doc(db, 'users', uid));
      await setDoc(doc(db, 'users', uid), {
        uid,
        email: authEmailVal,
        name: authName.trim() || t('register_flow.default_owner_name'),
        role: 'owner',
        createdAt: userDoc.exists() ? userDoc.data().createdAt : new Date()
      }, { merge: true });

      await setDoc(doc(db, 'businesses', uid), {
        ownerId: uid,
        // Written under both names on purpose: older data uses `businessType`
        // and the catalog/QR/team code reads that one. Keeping them in sync at
        // the source is what stops a salon from behaving like a solo master.
        type: roleType,
        businessType: roleType,
        name: businessName || authName || t('register_flow.default_business_name'),
        category: selectedCategories[0] || '',
        categories: selectedCategories,
        subCategories: subCategories,
        phone: phone,
        address: address,
        city: city,
        lat: lat,
        lng: lng,
        ...(businessNumber ? { businessNumber } : {}),
        ...(about ? { about } : {}),
        ...(instagramHandle ? { instagram: instagramHandle } : {}),
        ...(certificates.length > 0 ? { certificates } : {}),
        status: 'active',
        // K-0049: каждый новый мастер получает пробный период сам, без ручной
        // правки. plan/trialEndsAt — денормализованы, чтобы админ и напоминалки
        // видели дату без пересчёта.
        plan: 'trial',
        trialStartAt: new Date(),
        trialDays: trialDaysConfig,
        trialEndsAt: new Date(Date.now() + trialDaysConfig * 86400000),
        bookingMode: 'instant',
        createdAt: new Date()
      }, { merge: true });

      router.push('/dashboard');
    } catch (err) {
      console.error(err);
      setError(t('auth.registration_failed'));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleAuth = async () => {
    if (typeof window !== 'undefined' && 'ReactNativeWebView' in window) {
      console.log('[Native Auth] Triggering Google Login via React Native Bridge');
      try {
        const win = window as unknown as { ReactNativeWebView?: { postMessage: (msg: string) => void } };
        win.ReactNativeWebView?.postMessage(JSON.stringify({ type: 'PLEASE_LOGIN', provider: 'google' }));
      } catch (e) {
        console.error('WebView postMessage error', e);
      }
      // Works for already compiled TestFlight builds because url.includes('/login') is intercepted
      window.location.href = '/login?triggerNative=1&provider=google';
      return;
    }

    setError('');
    setLoading(true);
    const provider = new GoogleAuthProvider();
    try {
      await signInWithPopup(auth, provider);
      // After signIn, onAuthStateChanged will update user -> useEffect jumps to step 2
    } catch (err) {
      console.error(err);
      setError(t('auth.google_failed'));
    } finally {
      setLoading(false);
    }
  };

  const handleAppleAuth = async () => {
    if (typeof window !== 'undefined' && 'ReactNativeWebView' in window) {
      console.log('[Native Auth] Triggering Apple Login via React Native Bridge');
      try {
        const win = window as unknown as { ReactNativeWebView?: { postMessage: (msg: string) => void } };
        win.ReactNativeWebView?.postMessage(JSON.stringify({ type: 'PLEASE_LOGIN', provider: 'apple' }));
      } catch (e) {
        console.error('WebView postMessage error', e);
      }
      // Works for already compiled TestFlight builds because url.includes('/login') is intercepted
      window.location.href = '/login?triggerNative=1&provider=apple';
      return;
    }

    setError('');
    setLoading(true);
    const provider = new OAuthProvider('apple.com');
    provider.addScope('email');
    provider.addScope('name');
    try {
      await signInWithPopup(auth, provider);
    } catch (err) {
      console.error(err);
      setError(t('auth.apple_failed'));
    } finally {
      setLoading(false);
    }
  };

  const handleEmailAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!authEmail || !authPassword) return;
    setError('');
    setLoading(true);
    try {
      await createUserWithEmailAndPassword(auth, authEmail, authPassword);
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message || t('auth.registration_failed'));
      } else {
        setError(t('auth.registration_failed'));
      }
    } finally {
      setLoading(false);
    }
  };

  if (authLoading) return (
    <div className="min-h-[80vh] flex items-center justify-center">
      <Loader2 className="animate-spin w-10 h-10 text-primary" />
    </div>
  );

  return (
    <div className="min-h-[100dvh] flex flex-col bg-linear-to-b from-background via-background to-primary/5">
      {/* Top Bar with Step Counter */}
      <div className="sticky top-0 z-30 bg-background/80 backdrop-blur-xl border-b border-border/50">
        <div className="container mx-auto max-w-lg px-4 py-3">
          <div className="flex items-center justify-between mb-2">
            {step > 1 ? (
              <button
                onClick={() => setStep(s => Math.max(user ? 2 : 1, s - 1))}
                className="w-10 h-10 flex items-center justify-center rounded-xl bg-muted hover:bg-muted/80 active:scale-95 transition-all"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
            ) : (
              <div className="w-10" />
            )}
            <span className="text-sm font-bold text-muted-foreground">
              {t('auth.step')} {step}/{TOTAL_STEPS}
            </span>
            <div className="w-10" />
          </div>
          {/* Progress Bar */}
          <div className="flex gap-1.5">
            {Array.from({ length: TOTAL_STEPS }, (_, i) => i + 1).map(i => (
              <div
                key={i}
                className={`h-1.5 flex-1 rounded-full transition-all duration-500 ${
                  step > i ? 'bg-primary' : step === i ? 'bg-primary animate-pulse' : 'bg-muted'
                }`}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col container mx-auto max-w-lg px-4 py-6 pb-32">

        {/* Error Alert */}
        {error && (
          <div className="mb-4 p-3.5 bg-red-500/10 border border-red-500/20 text-red-500 rounded-2xl flex items-center gap-3 text-sm font-medium animate-in fade-in slide-in-from-top-2">
            <AlertCircle className="w-5 h-5 shrink-0" />
            <span className="flex-1">{error}</span>
            <button onClick={() => setError('')} className="p-1 hover:bg-red-500/10 rounded-lg">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* ========== STEP 1: AUTH ========== */}
        {step === 1 && !user && (
          <div className="flex flex-col gap-6 animate-in fade-in slide-in-from-right-4 duration-300">
            <div className="text-center">
              <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Sparkles className="w-10 h-10 text-primary" />
              </div>
              <h1 className="text-2xl sm:text-3xl font-black mb-2">{t('register_flow.auth_title')}</h1>
              <p className="text-muted-foreground text-sm">{t('register_flow.auth_subtitle')}</p>
            </div>

            {/* Google */}
            <button
              onClick={handleGoogleAuth}
              disabled={loading}
              className="w-full bg-white text-black border border-gray-200 shadow-sm font-bold py-4 rounded-2xl flex items-center justify-center gap-3 transition-all active:scale-[0.97] disabled:opacity-50 min-h-[56px]"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
              </svg>
              {t('auth.google_btn')}
            </button>

            {/* Apple */}
            <button
              onClick={handleAppleAuth}
              disabled={loading}
              className="w-full bg-black text-white border border-gray-800 shadow-sm font-bold py-4 rounded-2xl flex items-center justify-center gap-3 transition-all active:scale-[0.97] disabled:opacity-50 min-h-[56px]"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
              </svg>
              {t('auth.apple_btn')}
            </button>

            {/* Separator */}
            <div className="relative">
              <div className="absolute inset-0 flex items-center"><span className="w-full border-t border-border" /></div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-background px-4 text-muted-foreground font-bold tracking-widest">{t('auth.or_email')}</span>
              </div>
            </div>

            {/* Email/Password */}
            <form onSubmit={handleEmailAuth} className="flex flex-col gap-3">
              <div className="relative">
                <Mail className="absolute start-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground pointer-events-none z-10" />
                <input
                  type="email"
                  required
                  value={authEmail}
                  onChange={e => setAuthEmail(e.target.value)}
                  placeholder={t('auth.email_placeholder')}
                  className="w-full bg-background border border-input rounded-xl ps-12 pe-4 py-3.5 outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all font-medium min-h-[52px]"
                />
              </div>
              <div className="relative">
                <Lock className="absolute start-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground pointer-events-none z-10" />
                <input
                  type="password"
                  required
                  minLength={6}
                  value={authPassword}
                  onChange={e => setAuthPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full bg-background border border-input rounded-xl ps-12 pe-4 py-3.5 outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all font-medium min-h-[52px]"
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-foreground text-background font-bold py-4 rounded-2xl transition-all disabled:opacity-50 flex items-center justify-center gap-2 min-h-[56px]"
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : t('register_flow.email_register')}
              </button>
            </form>
          </div>
        )}

        {/* ========== STEP 2: ROLE ========== */}
        {step === 2 && (
          <div className="flex flex-col gap-6 animate-in fade-in slide-in-from-right-4 duration-300">
            {/* Show logged-in badge */}
            {user && (
              <div className="bg-emerald-500/10 border border-emerald-500/20 text-emerald-600 rounded-2xl p-4 flex items-center gap-3 font-bold text-sm">
                <Check className="w-5 h-5 shrink-0" />
                {t('register_flow.logged_as', { name: user.displayName || user.email || '' })}
              </div>
            )}

            <div className="text-center">
              <h1 className="text-2xl sm:text-3xl font-black mb-2">{t('auth.business_type')}</h1>
              <p className="text-muted-foreground text-sm">{t('business_landing.badge')}</p>
            </div>

            <div className="flex flex-col gap-3">
              {/* Salon Option */}
              <button
                onClick={() => setRoleType('salon')}
                className={`relative w-full p-5 rounded-2xl flex items-center gap-4 border-2 transition-all active:scale-[0.98] ${
                  roleType === 'salon'
                    ? 'border-primary bg-primary/5 shadow-lg shadow-primary/10'
                    : 'border-border hover:border-primary/40 bg-card'
                }`}
              >
                <div className={`w-14 h-14 rounded-2xl flex items-center justify-center shrink-0 transition-colors ${
                  roleType === 'salon' ? 'bg-primary/15 text-primary' : 'bg-muted text-muted-foreground'
                }`}>
                  <Building2 className="w-7 h-7" />
                </div>
                <div className="flex-1 text-start">
                  <span className="font-bold text-base block">{t('auth.type_salon')}</span>
                  <span className="text-xs text-muted-foreground">{t('business_landing.feat_2_desc').slice(0, 50)}...</span>
                </div>
                {roleType === 'salon' && (
                  <div className="absolute top-3 end-3 w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center">
                    <Check className="w-4 h-4" />
                  </div>
                )}
              </button>

              {/* Master Option */}
              <button
                onClick={() => setRoleType('master')}
                className={`relative w-full p-5 rounded-2xl flex items-center gap-4 border-2 transition-all active:scale-[0.98] ${
                  roleType === 'master'
                    ? 'border-primary bg-primary/5 shadow-lg shadow-primary/10'
                    : 'border-border hover:border-primary/40 bg-card'
                }`}
              >
                <div className={`w-14 h-14 rounded-2xl flex items-center justify-center shrink-0 transition-colors ${
                  roleType === 'master' ? 'bg-primary/15 text-primary' : 'bg-muted text-muted-foreground'
                }`}>
                  <UserCircle2 className="w-7 h-7" />
                </div>
                <div className="flex-1 text-start">
                  <span className="font-bold text-base block">{t('auth.type_master')}</span>
                  <span className="text-xs text-muted-foreground">{t('business_landing.feat_1_desc').slice(0, 50)}...</span>
                </div>
                {roleType === 'master' && (
                  <div className="absolute top-3 end-3 w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center">
                    <Check className="w-4 h-4" />
                  </div>
                )}
              </button>
            </div>
          </div>
        )}

        {/* ========== STEP 3: CATEGORY + SUBCATEGORIES ========== */}
        {step === 3 && (
          <div className="flex flex-col gap-5 animate-in fade-in slide-in-from-right-4 duration-300">
            <div className="text-center">
              <h1 className="text-2xl sm:text-3xl font-black mb-2">{t('auth.select_category')}</h1>
              <p className="text-muted-foreground text-sm">
                {t('register.multi_category_hint')}
              </p>
            </div>

            {/* Selected count badge */}
            {selectedCategories.length > 0 && (
              <div className="flex justify-center">
                <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-primary/10 text-primary rounded-full text-sm font-bold">
                  <Check className="w-4 h-4" />
                  {t('register.selected_count', { count: selectedCategories.length })}
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-2.5">
              {CATEGORIES.filter(c => c.key !== 'other').map(cat => {
                const selected = selectedCategories.includes(cat.key);
                return (
                  <button
                    key={cat.key}
                    onClick={() => toggleCategory(cat.key)}
                    className={`relative p-4 rounded-2xl border-2 flex flex-col items-center gap-2.5 transition-all active:scale-[0.96] min-h-[88px] ${
                      selected
                        ? 'border-primary bg-primary/10 shadow-md shadow-primary/10'
                        : 'border-border hover:border-primary/40 bg-card'
                    }`}
                  >
                    <span className="text-3xl">{cat.emoji}</span>
                    <span className="font-bold text-sm text-center leading-tight">{t(`categories.${cat.key}`)}</span>
                    {selected && (
                      <div className="absolute top-2 end-2 w-5 h-5 rounded-full bg-primary text-primary-foreground flex items-center justify-center">
                        <Check className="w-3 h-3" />
                      </div>
                    )}
                  </button>
                );
              })}
              {/* "Other" button */}
              <button
                onClick={() => toggleCategory('other')}
                className={`relative p-4 rounded-2xl border-2 flex flex-col items-center gap-2.5 transition-all active:scale-[0.96] min-h-[88px] border-dashed ${
                  selectedCategories.includes('other')
                    ? 'border-primary bg-primary/10'
                    : 'border-border hover:border-primary/40 bg-card'
                }`}
              >
                <span className="text-3xl">📝</span>
                <span className="font-bold text-sm text-center leading-tight">{t('categories.other')}</span>
                {selectedCategories.includes('other') && (
                  <div className="absolute top-2 end-2 w-5 h-5 rounded-full bg-primary text-primary-foreground flex items-center justify-center">
                    <Check className="w-3 h-3" />
                  </div>
                )}
              </button>
            </div>

            {/* Sub-categories section (inline) */}
            {selectedCategories.length > 0 && availableSubs.length > 0 && (
              <div className="mt-2 space-y-3">
                <h3 className="text-sm font-black text-muted-foreground">{t('auth.select_subcategories')}</h3>
                {CATEGORIES.filter(c => selectedCategories.includes(c.key) && c.subs.length > 0).map(cat => (
                  <div key={cat.key} className="bg-card rounded-2xl border border-border p-4">
                    <h3 className="font-bold text-sm mb-3 flex items-center gap-2">
                      <span>{cat.emoji}</span> {t(`categories.${cat.key}`)}
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {cat.subs.map(sub => {
                        const selected = subCategories.includes(sub.key);
                        return (
                          <button
                            key={sub.key}
                            onClick={() => {
                              if (selected) setSubCategories(prev => prev.filter(s => s !== sub.key));
                              else setSubCategories(prev => [...prev, sub.key]);
                            }}
                            className={`px-3.5 py-2.5 rounded-xl border text-sm font-bold transition-all active:scale-95 flex items-center gap-1.5 ${
                              selected
                                ? 'border-primary bg-primary text-primary-foreground shadow-md shadow-primary/20'
                                : 'border-border hover:border-primary/50 text-muted-foreground bg-background'
                            }`}
                          >
                            <span>{sub.emoji}</span>
                            {t(`categories.${sub.key}`)}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}

                {/* Custom subcategories */}
                {subCategories.filter(s => !availableSubs.find(as => as.key === s)).map(customSub => (
                  <div key={customSub} className="inline-flex items-center gap-2 px-4 py-2.5 bg-primary text-primary-foreground rounded-xl text-sm font-bold">
                    {customSub}
                    <button
                      onClick={() => setSubCategories(prev => prev.filter(s => s !== customSub))}
                      className="w-5 h-5 bg-primary-foreground/20 rounded-full flex items-center justify-center"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}

                {/* Custom service input */}
                <div className="bg-card rounded-2xl border border-border p-4">
                  <label className="text-sm font-bold text-muted-foreground mb-2 block">{t('register.custom_service_label')}</label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={customSubCategory}
                      onChange={e => setCustomSubCategory(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          if (customSubCategory.trim() && !subCategories.includes(customSubCategory.trim())) {
                            setSubCategories(prev => [...prev, customSubCategory.trim()]);
                            setCustomSubCategory('');
                          }
                        }
                      }}
                      placeholder={t('auth.add_custom_service')}
                      className="flex-1 bg-background border border-input rounded-xl px-4 py-3 outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 font-medium text-sm transition-all min-h-[48px]"
                    />
                    <button
                      onClick={() => {
                        if (customSubCategory.trim() && !subCategories.includes(customSubCategory.trim())) {
                          setSubCategories(prev => [...prev, customSubCategory.trim()]);
                          setCustomSubCategory('');
                        }
                      }}
                      className="bg-primary text-primary-foreground px-4 rounded-xl font-bold flex items-center gap-1.5 active:scale-95 transition-all min-h-[48px] shrink-0"
                    >
                      <Plus className="w-4 h-4" />
                      {t('auth.add')}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ========== STEP 4: ADDRESS (Nominatim) ========== */}
        {step === 4 && (
          <div className="flex flex-col gap-5 animate-in fade-in slide-in-from-right-4 duration-300">
            <div className="text-center">
              <h1 className="text-2xl sm:text-3xl font-black mb-2">{t('auth.address_label')}</h1>
              <p className="text-muted-foreground text-sm">{t('register.address_hint')}</p>
            </div>

            <div className="bg-card rounded-2xl border border-border p-5">
              <label className="text-sm font-bold text-muted-foreground block mb-2">{t('auth.address_label')}</label>
              
              {/* Address Autocomplete Input */}
              <div className="relative mb-3">
                <MapPin className="absolute start-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground pointer-events-none z-10" />
                <input
                  type="text"
                  value={address}
                  onChange={e => searchAddress(e.target.value)}
                  onFocus={() => { if (addressSuggestions.length > 0) setShowSuggestions(true); }}
                  onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                  placeholder={t('auth.address_placeholder')}
                  className="w-full bg-background border border-input rounded-xl ps-12 pe-4 py-4 outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all font-medium text-base min-h-[56px]"
                />
                {/* Suggestions Dropdown */}
                {showSuggestions && addressSuggestions.length > 0 && (
                  <div className="absolute top-full left-0 right-0 z-30 mt-1 bg-card border border-border rounded-xl shadow-xl overflow-hidden max-h-48 overflow-y-auto">
                    {addressSuggestions.map((s, i) => (
                      <button
                        key={i}
                        onMouseDown={() => selectAddress(s)}
                        className="w-full text-start px-4 py-2.5 text-sm hover:bg-primary/10 transition-all flex items-start gap-2 border-b border-border/50 last:border-0"
                      >
                        <MapPin className="w-3.5 h-3.5 mt-0.5 text-primary shrink-0" />
                        <span className="line-clamp-2">{s.display_name}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Detect Location Button */}
              <button
                onClick={detectLocation}
                disabled={geoLoading}
                className={`flex items-center gap-2 px-4 py-3 rounded-xl border font-bold text-sm transition-all w-full justify-center ${
                  lat && lng
                    ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-600'
                    : 'bg-background border-border hover:border-primary/50 text-muted-foreground'
                }`}
              >
                {geoLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Navigation className="w-4 h-4" />}
                {t('register_flow.detect_location')}
              </button>

              {/* Show resolved location */}
              {lat && lng && (
                <div className="mt-3 flex items-center gap-2 text-sm text-emerald-600 font-bold">
                  <Check className="w-4 h-4" />
                  <span>📍 {lat.toFixed(4)}, {lng.toFixed(4)}</span>
                  {city && <span className="text-muted-foreground ms-1">({city})</span>}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ========== STEP 5: PROFILE INFO & FINALIZE ========== */}
        {step === 5 && (
          <div className="flex flex-col gap-5 animate-in fade-in slide-in-from-right-4 duration-300">
            <div className="text-center">
              <h1 className="text-2xl sm:text-3xl font-black mb-2">{t('auth.finish')}</h1>
            </div>

            <div className="bg-amber-500/10 border border-amber-500/20 text-amber-700/80 rounded-2xl p-4 text-sm font-medium space-y-2">
              <h4 className="font-bold text-amber-700 flex items-center gap-2">
                <AlertCircle className="w-4 h-4" /> {t('register_welcome.attention_title')}
              </h4>
              <p>{t('register_welcome.welcome_text')}</p>
              <p dangerouslySetInnerHTML={{ __html: t('register_welcome.instant_info') }} />
            </div>

            {/* Business Name & Phone */}
            <div className="bg-card rounded-2xl border border-border p-5 space-y-4">
              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-bold">{t('auth.business_name')}</label>
                <div className="relative">
                  <Building2 className="absolute start-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground pointer-events-none" />
                  <input
                    type="text"
                    required
                    value={businessName}
                    onChange={e => setBusinessName(e.target.value)}
                    placeholder={t('auth.business_name_placeholder')}
                    className="w-full bg-background border border-input rounded-xl ps-12 pe-4 py-3.5 outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all font-medium min-h-[52px]"
                  />
                </div>
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-bold">{t('auth.phone')}</label>
                <div className="relative">
                  <Phone className="absolute start-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground pointer-events-none" />
                  <input
                    type="tel"
                    required
                    value={phone}
                    onChange={e => setPhone(e.target.value)}
                    placeholder={t('auth.phone_placeholder')}
                    className="w-full bg-background border border-input rounded-xl ps-12 pe-4 py-3.5 outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all font-medium min-h-[52px]"
                  />
                </div>
              </div>
            </div>

            {/* Additional Business Info (all optional) */}
            <div className="bg-card rounded-2xl border border-border p-5 space-y-4">
              <h3 className="text-sm font-black text-muted-foreground uppercase tracking-wider flex items-center gap-2">
                <FileText className="w-4 h-4" />
                {t('register_flow.about_label')}
                <span className="text-xs font-medium text-muted-foreground/60 normal-case tracking-normal">{t('register_flow.optional')}</span>
              </h3>

              {/* About / Bio */}
              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-bold">{t('register_flow.about_label')}</label>
                <textarea
                  value={about}
                  onChange={e => { if (e.target.value.length <= 300) setAbout(e.target.value); }}
                  placeholder={t('register_flow.about_placeholder')}
                  rows={3}
                  className="w-full bg-background border border-input rounded-xl px-4 py-3 outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all font-medium text-sm min-h-[80px] resize-none"
                />
                <span className="text-xs text-muted-foreground text-end">
                  {t('register_flow.about_chars', { count: about.length, max: 300 })}
                </span>
              </div>

              {/* Business Number */}
              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-bold flex items-center gap-2">
                  {t('register_flow.business_number_label')}
                  <span className="text-xs font-medium text-muted-foreground/60">{t('register_flow.optional')}</span>
                </label>
                <p className="text-xs text-muted-foreground -mt-1">{t('register_flow.business_number_hint')}</p>
                <div className="relative">
                  <Hash className="absolute start-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground pointer-events-none" />
                  <input
                    type="text"
                    inputMode="numeric"
                    maxLength={9}
                    value={businessNumber}
                    onChange={e => setBusinessNumber(e.target.value.replace(/\D/g, '').slice(0, 9))}
                    placeholder={t('register_flow.business_number_placeholder')}
                    className="w-full bg-background border border-input rounded-xl ps-12 pe-4 py-3.5 outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all font-medium min-h-[52px]"
                  />
                </div>
              </div>

              {/* Instagram */}
              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-bold flex items-center gap-2">
                  <span className="text-base">📸</span>
                  {t('register_flow.instagram_label')}
                  <span className="text-xs font-medium text-muted-foreground/60">{t('register_flow.optional')}</span>
                </label>
                <div className="relative">
                  <span className="absolute start-4 top-1/2 -translate-y-1/2 text-muted-foreground font-bold text-sm pointer-events-none">@</span>
                  <input
                    type="text"
                    value={instagramHandle}
                    onChange={e => setInstagramHandle(e.target.value.replace(/^@/, ''))}
                    placeholder={t('register_flow.instagram_placeholder')}
                    className="w-full bg-background border border-input rounded-xl ps-10 pe-4 py-3.5 outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all font-medium min-h-[52px]"
                  />
                </div>
              </div>
            </div>

            {/* Certificates Upload */}
            <div className="bg-card rounded-2xl border border-border p-5 space-y-3">
              <h3 className="text-sm font-black text-muted-foreground uppercase tracking-wider flex items-center gap-2">
                <Upload className="w-4 h-4" />
                {t('register_flow.certificates_label')}
                <span className="text-xs font-medium text-muted-foreground/60 normal-case tracking-normal">{t('register_flow.optional')}</span>
              </h3>
              <p className="text-xs text-muted-foreground">{t('register_flow.certificates_hint')}</p>
              <p className="text-xs text-primary font-bold">{t('register_flow.verified_badge_hint')}</p>

              {/* Uploaded certificates preview */}
              {certificates.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {certificates.map((url, i) => (
                    <div key={i} className="relative w-20 h-20 rounded-xl overflow-hidden border-2 border-emerald-500/30 bg-secondary">
                      <img src={url} alt={`cert-${i}`} className="w-full h-full object-cover" />
                      <button
                        onClick={() => setCertificates(prev => prev.filter((_, idx) => idx !== i))}
                        className="absolute top-0.5 end-0.5 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center"
                      >
                        <X className="w-3 h-3" />
                      </button>
                      <div className="absolute bottom-0 inset-x-0 bg-emerald-500 text-white text-[9px] text-center font-bold py-0.5">✅</div>
                    </div>
                  ))}
                </div>
              )}

              {certificates.length < 3 && (
                <>
                  <input
                    ref={certInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file || !user) return;
                      setCertUploading(true);
                      try {
                        const storage = getStorage();
                        const storageRef = ref(storage, `certificates/${user.uid}/${Date.now()}_${file.name}`);
                        await uploadBytes(storageRef, file);
                        const url = await getDownloadURL(storageRef);
                        setCertificates(prev => [...prev, url]);
                      } catch (err) {
                        console.error('Certificate upload failed:', err);
                        setError(t('register_flow.upload_error'));
                      } finally {
                        setCertUploading(false);
                        if (certInputRef.current) certInputRef.current.value = '';
                      }
                    }}
                  />
                  <button
                    onClick={() => certInputRef.current?.click()}
                    disabled={certUploading}
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl border-2 border-dashed border-border hover:border-primary/50 text-muted-foreground hover:text-primary transition-all font-bold text-sm min-h-[52px] disabled:opacity-50"
                  >
                    {certUploading ? (
                      <><Loader2 className="w-4 h-4 animate-spin" /> {t('register_flow.uploading')}</>
                    ) : (
                      <><Upload className="w-4 h-4" /> {t('register_flow.upload_cert')}</>
                    )}
                  </button>
                </>
              )}
            </div>

            {/* Summary Card */}
            <div className="bg-card rounded-2xl border border-border p-5 space-y-2">
              <h3 className="text-sm font-black text-muted-foreground uppercase tracking-wider">{t('register_flow.summary')}</h3>
              <div className="text-sm space-y-1">
                <p><span className="font-bold">{t('auth.business_type')}:</span> {roleType === 'salon' ? t('auth.type_salon') : t('auth.type_master')}</p>
                {selectedCategories.length > 0 && (
                  <p><span className="font-bold">{t('auth.select_category')}:</span> {selectedCategories.map(c => t(`categories.${c}`)).join(', ')}</p>
                )}
                {address && (
                  <p><span className="font-bold">{t('auth.address_label')}:</span> {address.slice(0, 80)}{address.length > 80 ? '...' : ''}</p>
                )}
                {businessNumber && (
                  <p><span className="font-bold">{t('register_flow.business_number_label')}:</span> {businessNumber}</p>
                )}
                {certificates.length > 0 && (
                  <p><span className="font-bold">{t('register_flow.certificates_label')}:</span> {certificates.length} ✅</p>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Fixed Bottom Action Bar */}
      <div className="fixed bottom-0 left-0 right-0 z-30 bg-background/90 backdrop-blur-xl border-t border-border/50 safe-area-inset-bottom">
        <div className="container mx-auto max-w-lg px-4 py-3 flex gap-2.5">
          {step >= 3 && step <= 4 && (
            <button
              onClick={() => setStep(s => s + 1)}
              className="flex-1 bg-muted text-foreground font-bold py-4 rounded-2xl active:scale-[0.97] transition-all text-sm min-h-[56px]"
            >
              {t('auth.skip')}
            </button>
          )}
          {step >= 2 && step < TOTAL_STEPS && (
            <button
              onClick={handleNextStep}
              className="flex-1 bg-primary text-primary-foreground font-black py-4 rounded-2xl active:scale-[0.97] transition-all flex items-center justify-center gap-2 text-base min-h-[56px] shadow-lg shadow-primary/25"
            >
              {t('auth.next')}
              <ChevronRight className="w-5 h-5" />
            </button>
          )}
          {step === TOTAL_STEPS && (
            <button
              onClick={finalizeRegistration}
              disabled={loading}
              className="flex-1 bg-primary text-primary-foreground font-black py-4 rounded-2xl active:scale-[0.97] transition-all flex items-center justify-center gap-2 text-base min-h-[56px] shadow-lg shadow-primary/25 disabled:opacity-50"
            >
              {loading ? <Loader2 className="w-6 h-6 animate-spin" /> : (
                <>
                  <Sparkles className="w-5 h-5" />
                  {t('auth.finish')}
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function BusinessRegister() {
  return (
    <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><Loader2 className="w-10 h-10 animate-spin text-primary" /></div>}>
      <BusinessRegisterContent />
    </Suspense>
  );
}
